import React, { useState } from "react";
import Text from "@src/lib/ui/Text";
import { Button } from "@src/lib/ui/Button";
import { Popover, PopoverContent, PopoverTrigger } from "@src/lib/ui/Popover";
import ChevronDownIcon from "@src/lib/icons/12px/chevron-down";
import PenOutlineIcon from "@src/lib/icons/18px/pen-outline";
import ArchiveOutlineIcon from "@src/lib/icons/18px/archive-outline";
import AgentAvatar from "@src/features/agents/AgentAvatar";
import AgentDialog from "@src/features/agents/AgentDialog";
import ArchiveAgentDialog from "@src/features/agents/ArchiveAgentDialog";
import { OpusAgentFieldsFragment } from "./agentPanel.generated";

type Props = {
  agentId: string;
  agent: OpusAgentFieldsFragment | null;
  // Archiving goes through the shared confirmation dialog; unarchiving is
  // immediate.
  onUnarchive: () => void;
};

// The agent panel's title: the bot and name open the agent's details (what
// it does, its instructions) with Edit and Archive, so the agent can be
// managed from its own chat instead of only from the rail's row menu.
const AgentPanelHeaderDetails: React.FC<Props> = ({
  agentId,
  agent,
  onUnarchive,
}) => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmingArchive, setConfirmingArchive] = useState(false);

  if (!agent) return <AgentAvatar agent={{ id: agentId }} size="icon" />;

  const archived = Boolean(agent.archivedAt);
  // The bot shows what the chat is doing (thinking, its finishing beat);
  // an archived agent rests, dimmed.
  const dim = archived ? "opacity-60" : undefined;
  const createdAt = new Date(agent.createdAt).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="-ml-1.5 flex min-w-0 items-center gap-2 rounded-lg py-1 pr-2 pl-1.5 transition-colors hover:bg-state-hover focus-visible:ring-3 focus-visible:ring-brand-border focus-visible:outline-none active:bg-state-pressed data-[state=open]:bg-state-hover"
            aria-label={`About ${agent.name}`}
            data-testid="agent-panel-details-trigger"
          >
            <AgentAvatar agent={agent} size="icon" className={dim} />
            <Text
              variant="compact-title"
              className="block min-w-0 truncate text-text-primary"
            >
              {agent.name}
            </Text>
            <ChevronDownIcon className="size-3 shrink-0 text-icon-tertiary" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="flex w-85 flex-col gap-4 p-4"
          // Focus the popover itself, not its first button: Archive would
          // otherwise open ringed, reading as the default action.
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            (event.currentTarget as HTMLElement | null)?.focus();
          }}
          data-testid="agent-panel-details"
        >
          <div className="flex items-center gap-3">
            <AgentAvatar agent={agent} size="lg" className={dim} />
            <div className="flex min-w-0 flex-col">
              <Text
                variant="compact-title"
                className="text-text-primary"
                ellipsis
              >
                {agent.name}
              </Text>
              <Text variant="helper-description" className="text-text-tertiary">
                {archived ? "Archived" : `Created ${createdAt}`}
              </Text>
            </div>
          </div>
          {agent.template?.description ? (
            <Text
              variant="compact-description"
              className="text-text-secondary"
              multiline
            >
              {agent.template.description}
            </Text>
          ) : null}
          {agent.schedule?.enabled && (
            <div className="flex flex-col gap-1.5">
              <Text variant="helper-title" className="text-text-secondary">
                Schedule
              </Text>
              <Text
                variant="helper-description"
                multiline
                data-testid="agent-panel-details-schedule"
              >
                {agent.schedule.summary}
              </Text>
            </div>
          )}
          <div className="flex flex-col gap-1.5">
            <Text variant="helper-title" className="text-text-secondary">
              Instructions
            </Text>
            <div className="max-h-56 overflow-y-auto rounded-lg bg-bg-secondary p-3">
              <Text
                variant="helper-description"
                multiline
                className="whitespace-pre-wrap text-text-primary"
                data-testid="agent-panel-details-instructions"
              >
                {agent.instructions || "No instructions yet."}
              </Text>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setOpen(false);
                if (archived) onUnarchive();
                else setConfirmingArchive(true);
              }}
              data-testid="agent-panel-details-archive"
            >
              <ArchiveOutlineIcon />
              {archived ? "Unarchive" : "Archive"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setOpen(false);
                setEditing(true);
              }}
              data-testid="agent-panel-details-edit"
            >
              <PenOutlineIcon />
              Edit
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      {editing && (
        <AgentDialog agent={agent} onClose={() => setEditing(false)} />
      )}
      {confirmingArchive && (
        <ArchiveAgentDialog
          open={confirmingArchive}
          onOpenChange={setConfirmingArchive}
          agent={agent}
        />
      )}
    </>
  );
};

export default AgentPanelHeaderDetails;
